// src/components/sidebar/AddPoliticianPopup.jsx
import React, { useState, useEffect, useRef } from "react";
import LiveFeed from "./LiveFeed";
import "../../styles/sidebar/AddPoliticianPopup.css";

const API_FETCH = "https://api.anti-bot.ai";

export default function AddPoliticianPopup({ onClose, onAdded }) {
  const [searchInput, setSearchInput] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeUsername, setActiveUsername] = useState(null);
  const [error, setError] = useState("");
  const [minimized, setMinimized] = useState(false);
  const [doneMsg, setDoneMsg] = useState("");

  // כדי שלא נאבד selectedUser בתוך setTimeout (stale closure)
  const selectedUserRef = useRef(null);
  useEffect(() => {
    selectedUserRef.current = selectedUser;
  }, [selectedUser]);

  // כדי לנקות timeout אם סוגרים/יוצאים
  const closeTimeoutRef = useRef(null);
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    };
  }, []);

  // סגור עם ESC רק כשלא ממוזער
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape" && !minimized) onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose, minimized]);

  const handleSearch = async () => {
    const name = searchInput.trim();
    if (!name) {
      setError("נא להזין שם לחיפוש");
      return;
    }

    setSearching(true);
    setError("");
    setSearchResults([]);
    setSelectedUser(null);

    try {
      const res = await fetch(
        `${API_FETCH}/api/targets/search?name=${encodeURIComponent(name)}`
      );

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      if (data?.success && Array.isArray(data.results) && data.results.length > 0) {
        setSearchResults(data.results);
      } else {
        setError("לא נמצאו תוצאות — נסה שם אחר");
      }
    } catch {
      setError("❌ שגיאה בחיבור לשרת");
    } finally {
      setSearching(false);
    }
  };

  const handleAdd = () => {
    if (!selectedUser) {
      setError("נא לבחור חשבון");
      return;
    }
    setError("");
    setActiveUsername(selectedUser.username);
  };

  const handleFeedDone = (data) => {
    const u = selectedUserRef.current;
    const username = data?.username ?? u?.username;

    setDoneMsg(`✅ @${username} נוסף!`);

    closeTimeoutRef.current = setTimeout(() => {
      onAdded?.({ username, name: u?.name, image: u?.image });
      onClose?.();
    }, 1500);
  };

  return (
    <>
      {/* ── Minimized Bar (מופיע רק כשממוזער) ── */}
      {minimized && (
        <div className="app-minimized-bar" dir="rtl">
          <div className="app-minimized-content">
            {doneMsg ? (
              <span className="app-minimized-done">{doneMsg}</span>
            ) : (
              <>
                <span className="app-minimized-spinner" />
                <span className="app-minimized-text">
                  מוסיף את @{activeUsername || selectedUser?.username}...
                </span>
              </>
            )}
          </div>

          <div className="app-minimized-actions">
            <button
              className="app-minimized-btn"
              onClick={() => setMinimized(false)}
              title="הרחב"
            >
              ⊡
            </button>
            <button
              className="app-minimized-btn close"
              onClick={onClose}
              title="סגור"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* ── Overlay + Popup נשארים MOUNTED כדי ש-LiveFeed ימשיך לרוץ ── */}
      <div
        className={`app-overlay ${minimized ? "is-minimized" : ""}`}
        onClick={(e) =>
          !minimized && e.target === e.currentTarget && onClose()
        }
      >
        <div className="app-popup" dir="rtl">
          <div className="app-popup-header">
            <div className="app-popup-title">
              <span className="app-popup-icon">＋</span>
              הוסף פוליטיקאי למעקב
            </div>

            <div className="app-popup-header-btns">
              {activeUsername && (
                <button
                  className="app-popup-minimize"
                  onClick={() => setMinimized(true)}
                  title="מזער"
                >
                  ⊟
                </button>
              )}
              <button className="app-popup-close" onClick={onClose}>
                ✕
              </button>
            </div>
          </div>

          <div className="app-popup-body">
            {/* שלב 1: חיפוש */}
            {!activeUsername && (
              <>
                <div className="app-popup-search-row">
                  <button
                    className="app-popup-btn-search"
                    onClick={handleSearch}
                    disabled={searching}
                  >
                    {searching ? "מחפש..." : "חפש"}
                  </button>

                  <input
                    className="app-popup-input"
                    placeholder="חפש לפי שם — לדוגמה: יאיר לפיד"
                    value={searchInput}
                    onChange={(e) => {
                      setSearchInput(e.target.value);
                      setError("");
                      setSearchResults([]);
                      setSelectedUser(null);
                    }}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    disabled={searching}
                    autoFocus
                  />
                </div>

                {error && <div className="app-popup-error">{error}</div>}

                {searchResults.length > 0 && (
                  <div className="app-popup-results">
                    <div className="app-popup-results-label">בחר חשבון:</div>

                    {searchResults.map((u) => (
                      <div
                        key={u.username || u.id}
                        className={`app-popup-result-item ${
                          selectedUser?.username === u.username ? "selected" : ""
                        }`}
                        onClick={() => setSelectedUser(u)}
                      >
                        {u.image ? (
                          <img
                            src={u.image}
                            alt={u.username}
                            className="app-popup-avatar"
                          />
                        ) : (
                          <div className="app-popup-avatar-placeholder">
                            {(u.name || u.username)?.[0]}
                          </div>
                        )}

                        <div className="app-popup-result-info">
                          <span className="app-popup-result-name">{u.name}</span>
                          <span className="app-popup-result-username">
                            @{u.username}
                          </span>
                        </div>

                        {selectedUser?.username === u.username && (
                          <span className="app-popup-check">✓</span>
                        )}
                      </div>
                    ))}

                    {selectedUser && (
                      <button className="app-popup-btn-add" onClick={handleAdd}>
                        הוסף את @{selectedUser.username} למעקב
                      </button>
                    )}
                  </div>
                )}
              </>
            )}

            {/* שלב 2: LiveFeed */}
            {activeUsername && (
              <>
                <div className="app-popup-minimize-hint">
                  💡 ניתן למזער את החלון ולהמשיך לגלוש בזמן שהתהליך רץ
                </div>
                <LiveFeed username={activeUsername} onDone={handleFeedDone} />
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}